// ============================================================
// AccountantDashboard Component
// ============================================================

import { Box, Typography, LinearProgress, Stack, Checkbox, FormControlLabel, FormGroup, CircularProgress } from '@mui/material';
import { FileText, Users, CreditCard, Clock, Target, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DashboardGrid } from '@/components/dashboard/DashboardGrid';
import { StatCard } from '@/components/cards/StatCard';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { RecentActivityCard } from '@/components/dashboard/RecentActivityCard';
import { QuickActionCard } from '@/components/dashboard/QuickActionCard';
import { AppPieChart } from '@/components/charts/AppPieChart';
import { AppBarChart } from '@/components/charts/AppBarChart';
import { WelcomeBanner } from '@/components/dashboard/WelcomeBanner';
import { SectionHeader } from '@/components/dashboard/SectionHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { useAuth } from '@/hooks/useAuth';
import { useSchool } from '@/hooks/useSchool';
import useDashboardStore from '@/store/dashboardStore';
import { AnalyticsFilter } from '@/components/filters/AnalyticsFilter';
import { tokens } from '@/theme/tokens';
import { usePendingFees } from '@/hooks/useFees';
import { useTransactions } from '@/hooks/useTransactions';
import { useMemo } from 'react';
import { PATHS } from '@/router/paths';

function TargetProgress({ title, current, target, color = 'primary' }) {
  const percent = Math.min(Math.round((current / target) * 100), 100);
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{current} / {target}</Typography>
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={percent} 
        color={color} 
        sx={{ height: 8, borderRadius: 4, bgcolor: tokens.colors.grey[100] }} 
      />
    </Box>
  );
}

export function AccountantDashboard({ stats, charts, activities }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currentSchool } = useSchool();
  const dateRange = useDashboardStore((state) => state.dateRange);
  const setDateRange = useDashboardStore((state) => state.setDateRange);

  const { data: pendingFees = [], isLoading: isLoadingFees } = usePendingFees();
  const { data: transactions = [], isLoading: isLoadingTransactions } = useTransactions();

  const overdueFees = useMemo(() => pendingFees.filter(fee => fee.status === 'overdue'), [pendingFees]);
  const defaultersCount = overdueFees.length;

  const remainingStudents = useMemo(() => new Set(pendingFees.map(f => f.studentId)).size, [pendingFees]);
  const paidStudents = stats.totalStudents ? Math.max(0, stats.totalStudents - remainingStudents) : 0;

  const receiptsGenerated = transactions.length;

  const dynamicDefaultersByGrade = useMemo(() => {
    const gradeCounts = {};
    overdueFees.forEach((fee) => {
      const grade = fee.className || 'Unknown';
      if (!gradeCounts[grade]) {
        gradeCounts[grade] = 0;
      }
      gradeCounts[grade] += 1;
    });

    return Object.entries(gradeCounts)
      .map(([grade, students]) => ({ grade, students }))
      .sort((a, b) => {
        const numA = parseInt(a.grade.replace(/\D/g, '')) || 0;
        const numB = parseInt(b.grade.replace(/\D/g, '')) || 0;
        return numA - numB;
      });
  }, [overdueFees]);

  const dynamicReceiptModeCount = useMemo(() => {
    const counts = {};
    transactions.forEach(t => {
      const mode = t.paymentMethod || 'Other';
      if (!counts[mode]) counts[mode] = 0;
      counts[mode] += 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [transactions]);

  const formatCurrency = (val) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val);

  const dynamicRecentActivities = useMemo(() => {
    const sorted = [...transactions].sort((a, b) => new Date(b.date) - new Date(a.date));
    return sorted.slice(0, 5).map(t => ({
      id: t.id || t.receiptNo,
      type: 'fee',
      action: `Fee Collected - ${t.studentName}`,
      details: `${t.feeHeadName || 'Tuition Fee'} (${t.paymentMethod}) - ${formatCurrency(t.amount)}`,
      time: new Date(t.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
    }));
  }, [transactions]);

  return (
    <Box>
      <WelcomeBanner 
        user={user} 
        currentSchool={currentSchool} 
        action={<AnalyticsFilter value={dateRange} onChange={setDateRange} sx={{ bgcolor: 'background.paper', borderRadius: 1 }} />}
      />

      <SectionHeader title="Operational Overview" />
      <DashboardGrid columns={{ xs: 1, sm: 2, lg: 4 }}>
        <StatCard
          title="Students Paid"
          value={isLoadingFees ? '...' : paidStudents}
          icon={Users}
          color="success"
          onClick={() => navigate(PATHS.FEES_TRANSACTIONS)}
        />
        <StatCard
          title="Pending Students"
          value={isLoadingFees ? '...' : remainingStudents}
          icon={Clock}
          color="warning"
          onClick={() => navigate(PATHS.FEES_PENDING)}
        />
        <StatCard
          title="Receipts Generated"
          value={isLoadingTransactions ? '...' : receiptsGenerated}
          icon={FileText}
          color="primary"
          onClick={() => navigate(PATHS.FEES_TRANSACTIONS)}
        />
        <StatCard
          title="Defaulters Count"
          value={isLoadingFees ? '...' : defaultersCount}
          icon={AlertTriangle}
          color="error"
          onClick={() => navigate(PATHS.FEES_PENDING)}
        />
      </DashboardGrid>

      <DashboardGrid columns={{ xs: 1, lg: 3 }} sx={{ mt: 3 }}>
        <ChartCard title="Receipts by Mode (Count)">
          <AppPieChart data={dynamicReceiptModeCount} />
        </ChartCard>
        
        <ChartCard title="Defaulters by Grade">
          <AppBarChart 
            data={dynamicDefaultersByGrade} 
            xAxisKey="grade" 
            dataKey="students" 
            color={tokens.colors.warning.main}
          />
        </ChartCard>

        <WidgetContainer title="Today's Reminders" action={<Target size={18} color={tokens.colors.primary.main} />}>
          <Box sx={{ p: 2 }}>
            <TargetProgress 
              title="Receipts Goal" 
              current={receiptsGenerated} 
              target={150} 
              color="primary" 
            />
            
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: tokens.colors.text.secondary }}>
              Daily Tasks
            </Typography>
            <FormGroup>
              <FormControlLabel control={<Checkbox size="small" />} label={<Typography variant="body2">Follow up with Grade 5 defaulters</Typography>} />
              <FormControlLabel control={<Checkbox size="small" />} label={<Typography variant="body2">Deposit cleared cheques to bank</Typography>} />
              <FormControlLabel control={<Checkbox size="small" />} label={<Typography variant="body2">Review pending concession requests</Typography>} />
            </FormGroup>
          </Box>
        </WidgetContainer>
      </DashboardGrid>

      <DashboardGrid columns={{ xs: 1, lg: 2 }} sx={{ mt: 3 }}>
        <RecentActivityCard activities={dynamicRecentActivities} />

        <WidgetContainer title="Quick Actions">
          <Box sx={{ p: 1 }}>
            <DashboardGrid columns={2} gap={1}>
              <QuickActionCard title="Generate Receipt" icon={FileText} color="primary" path={PATHS.FEES_COLLECT} />
              <QuickActionCard title="Pending Payments" icon={Clock} color="warning" path={PATHS.FEES_PENDING} />
              <QuickActionCard title="Defaulters List" icon={AlertTriangle} color="error" path={PATHS.FEES_PENDING} />
              <QuickActionCard title="Concessions" icon={CreditCard} color="secondary" path={PATHS.FEE_REPORTS} />
            </DashboardGrid>
          </Box>
        </WidgetContainer>
      </DashboardGrid>
    </Box>
  );
}
